import { gql } from '@apollo/client';
import { useApolloClient, useQuery } from '@apollo/client/react';
import { Controller } from 'react-hook-form';
import { z } from 'zod';

import { Card } from '@repo/ui/Card';
import { Form } from '@repo/ui/Form';
import { Button } from '@repo/ui/Button';
import { useZodForm } from '@repo/hooks/useZodForm';
import { ApiKeySelectorField, type SelectorOption } from '@/components/ApiKeySelectorField';
import { useScrollToHash } from '@/hooks/useScrollToHash';
import GET_API_KEYS_FOR_DID from '@/features/dids/gql/getApiKeysForDid.gql';

export const API_KEY_ANCHOR = 'api-key';

const CHANGE_DID_API_KEY = gql`
  mutation ChangeDidApiKey($didId: ID!, $apiKeyId: ID) {
    changeDidApiKey(didId: $didId, apiKeyId: $apiKeyId) {
      did {
        id
      }
    }
  }
`;

const schema = z.object({
  apiKey: z.object({ label: z.string(), value: z.string() }),
});

interface ChangeDidApiKeyFormProps {
  didId: string;
  accountId: string;
}

const ChangeDidApiKeyForm = ({ didId, accountId }: ChangeDidApiKeyFormProps) => {
  const { data, loading, refetch } = useQuery(GET_API_KEYS_FOR_DID, {
    variables: { id: didId, first: 1 },
  });

  useScrollToHash(API_KEY_ANCHOR, !loading);

  const current = data?.apiKeysForDid?.edges[0]?.node;

  return (
    <div id={API_KEY_ANCHOR} className="w-full scroll-mt-32">
      <Card
        title="API Key"
        description="The API key this number sends and receives messages through."
        loading={loading && !data}
      >
        {data && (
          <ApiKeyForm
            // Remount when the current key changes so the form picks up the new default
            key={current?.id ?? 'none'}
            didId={didId}
            accountId={accountId}
            current={current ? { label: current.name, value: current.id } : undefined}
            onChanged={() => refetch()}
          />
        )}
      </Card>
    </div>
  );
};

const ApiKeyForm = ({
  didId,
  accountId,
  current,
  onChanged,
}: {
  didId: string;
  accountId: string;
  current: SelectorOption | undefined;
  onChanged: () => void;
}) => {
  const client = useApolloClient();

  const form = useZodForm({
    schema,
    defaultValues: current ? { apiKey: current } : undefined,
    onSubmit: async ({ apiKey }) => {
      await client.mutate({
        mutation: CHANGE_DID_API_KEY,
        variables: { didId, apiKeyId: apiKey.value },
      });
      onChanged();
    },
  });

  return (
    <Form {...form} column={1}>
      <Form.Success>API key was successfully changed.</Form.Success>
      <Form.Error>Unable to change the API key, please try again.</Form.Error>

      {!current && (
        <p className="text-sm sm:col-span-2">This number is not attached to an API key.</p>
      )}

      <Controller
        control={form.control}
        name="apiKey"
        render={({ field: { onChange, value } }) => (
          <ApiKeySelectorField
            // Editing: an empty value means the number has no API key; don't fill it in
            preselectDefault={false}
            accountId={accountId}
            value={value}
            onChange={onChange}
            error={form.formState.errors?.apiKey?.value}
          />
        )}
      />

      <div className="w-full sm:col-span-2">
        <Button type="submit">Save changes</Button>
      </div>
    </Form>
  );
};

export default ChangeDidApiKeyForm;
